import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Link from '@/lib/siteLink';
import { ChevronDown, Menu, X } from 'lucide-react';
import { DGTG_SERVICES, DGTG_INDUSTRY_PAGES } from './dgtgData';
import { useT } from '@/i18n/dgtgLang';
import { LangAlternates, LangSwitcher, LangSuggest } from '@/i18n/DGTGLangUI';

export default function DGTGNav() {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const svcRef = useRef(null);
  const indRef = useRef(null);
  const t = useT();

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setIndustriesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (svcRef.current && !svcRef.current.contains(e.target)) setServicesOpen(false);
      if (indRef.current && !indRef.current.contains(e.target)) setIndustriesOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isServiceActive = location.pathname.startsWith('/dgtg/service');
  const isIndustryActive = location.pathname.startsWith('/dgtg/industry');

  return (
    <>
    <LangAlternates />
    <LangSuggest />
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100" data-testid="dgtg-navbar">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/dgtg" className="flex items-center gap-2.5" data-testid="dgtg-logo-link">
          <img src="/dgtg-logo.png" alt="DGTG" className="h-8 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {/* Services dropdown */}
          <div className="relative" ref={svcRef}>
            <button
              onClick={() => { setServicesOpen(!servicesOpen); setIndustriesOpen(false); }}
              className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isServiceActive ? 'text-[#773DFF] font-bold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="dgtg-services-dropdown"
            >
              {t('Services')}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full start-0 mt-1 w-72 bg-white rounded-xl shadow-xl border border-gray-100 py-2">
                {DGTG_SERVICES.map((s) => (
                  <Link key={s.slug} to={`/dgtg/service/${s.slug}`} className="block px-4 py-2.5 hover:bg-[#773DFF]/5 transition-colors" data-testid={`nav-service-${s.slug}`}>
                    <p className="text-sm font-semibold text-gray-900">{t(s.title)}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{t(s.tagline)}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Industries dropdown */}
          <div className="relative" ref={indRef}>
            <button
              onClick={() => { setIndustriesOpen(!industriesOpen); setServicesOpen(false); }}
              className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isIndustryActive ? 'text-[#773DFF] font-bold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="dgtg-industries-dropdown"
            >
              {t('Industries')}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${industriesOpen ? 'rotate-180' : ''}`} />
            </button>
            {industriesOpen && (
              <div className="absolute top-full start-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 max-h-80 overflow-y-auto">
                {DGTG_INDUSTRY_PAGES.map((ind) => (
                  <Link key={ind.slug} to={`/dgtg/industry/${ind.slug}`} className="block px-4 py-2 hover:bg-[#773DFF]/5 transition-colors text-sm font-medium text-gray-700" data-testid={`nav-industry-${ind.slug}`}>
                    {t(ind.title)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/dgtg/about" className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive('/dgtg/about') ? 'text-[#773DFF] font-bold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
            {t('About us')}
          </Link>

          <Link to="/dgtg/blog" className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive('/dgtg/blog') || location.pathname.startsWith('/dgtg/blog') ? 'text-[#773DFF] font-bold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
            {t('News')}
          </Link>

          <Link to="/dgtg/partners" className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive('/dgtg/partners') ? 'text-[#773DFF] font-bold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
            {t('Partners')}
          </Link>
          <Link to="/dgtg/events" className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive('/dgtg/events') ? 'text-[#773DFF] font-bold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
            {t('Events')}
          </Link>
          <Link to="/dgtg/estimate" className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive('/dgtg/estimate') ? 'text-[#773DFF] font-bold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-testid="dgtg-nav-estimate">
            {t('Cost Calculator')}
          </Link>

          <LangSwitcher className="ms-2" />

          <Link to="/dgtg/contact" className="ms-2 bg-[#773DFF] text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-[#5B2ACC] transition-colors" data-testid="dgtg-nav-cta">
            {t('Get Started')}
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden p-2 text-gray-700" onClick={() => setMobileOpen(!mobileOpen)} data-testid="dgtg-mobile-menu">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-6 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
          <LangSwitcher className="mb-3" />
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('Services')}</p>
          {DGTG_SERVICES.map((s) => (
            <Link key={s.slug} to={`/dgtg/service/${s.slug}`} className="block py-2 text-sm text-gray-700 hover:text-[#773DFF]">{t(s.title)}</Link>
          ))}
          <hr className="my-2 border-gray-100" />
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('Industries')}</p>
          {DGTG_INDUSTRY_PAGES.map((ind) => (
            <Link key={ind.slug} to={`/dgtg/industry/${ind.slug}`} className="block py-1.5 text-sm text-gray-700 hover:text-[#773DFF]">{t(ind.title)}</Link>
          ))}
          <hr className="my-2 border-gray-100" />
          <Link to="/dgtg/about" className="block py-2 text-sm text-gray-700 hover:text-[#773DFF]">{t('About us')}</Link>
          <Link to="/dgtg/blog" className="block py-2 text-sm text-gray-700 hover:text-[#773DFF]">{t('News')}</Link>
          <Link to="/dgtg/estimate" className="block py-2 text-sm text-gray-700 hover:text-[#773DFF]">{t('Cost Calculator')}</Link>
          <Link to="/dgtg/grow" className="block py-2 text-sm text-gray-700 hover:text-[#773DFF]">{t('Grow Guides')}</Link>
          <Link to="/dgtg/partners" className="block py-2 text-sm text-gray-700 hover:text-[#773DFF]">{t('For publishers & creators')}</Link>
          <Link to="/dgtg/events" className="block py-2 text-sm text-gray-700 hover:text-[#773DFF]">{t('Events')}</Link>
          <Link to="/dgtg/contact" className="block py-2 text-sm font-semibold text-[#773DFF]">{t('Contact Us')}</Link>
        </div>
      )}
    </nav>
    </>
  );
}
